import { useEffect, useRef } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";

type Props = {
  /** Initial markdown content. Loaded once on mount; subsequent prop changes
   *  are ignored so the editor doesn't fight the user's typing. */
  initialMarkdown: string;
  /** Called whenever the editor content changes. Receives the *markdown*
   *  serialization, not HTML — markdown is what we persist in D1. */
  onChange: (markdown: string) => void;
  /** Called when the user right-clicks inside the editor. Use to open the
   *  shortcuts/command menu at the cursor. */
  onContextMenu?: (e: React.MouseEvent, editor: Editor) => void;
  /** Called when the editor mounts. Lets the parent grab a ref so it can
   *  trigger formatting commands from outside (e.g. the shortcuts menu). */
  onReady?: (editor: Editor) => void;
  placeholder?: string;
  className?: string;
};

/**
 * WYSIWYG markdown editor. Wraps Tiptap so the user types real text — bold
 * looks bold, headings look like headings — instead of seeing raw `**` /
 * `~~` markers. Storage stays as markdown, so existing cards keep working.
 */
export function RichEditor({
  initialMarkdown,
  onChange,
  onContextMenu,
  onReady,
  placeholder = "add notes, context, links...",
  className,
}: Props) {
  // The Tiptap editor is created once and reused for the lifetime of this
  // component. Re-creating it on every render would lose cursor / selection
  // / undo state.
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Tiptap's Link is more configurable than starter-kit's default.
        link: false,
        // We add TaskList separately; starter-kit doesn't ship task items.
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noreferrer noopener",
          class:
            "break-all text-lavender-400 underline decoration-lavender-200 decoration-1 underline-offset-2 hover:decoration-lavender-400",
        },
      }),
      Highlight.configure({
        HTMLAttributes: {
          class: "rounded bg-butter-100 px-0.5 text-ink",
        },
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "is-editor-empty",
      }),
      // tiptap-markdown handles markdown <-> doc roundtrip. We feed it the
      // raw markdown via content and read it back via storage.markdown.
      Markdown.configure({
        html: true, // allow inline <u>...</u> markers (Tiptap underline mark)
        tightLists: true,
        bulletListMarker: "-",
        linkify: false,
        breaks: false,
      }),
    ],
    content: initialMarkdown,
    onCreate: ({ editor }) => {
      onReady?.(editor);
    },
    onUpdate: ({ editor }) => {
      // editor.storage.markdown.getMarkdown() comes from tiptap-markdown.
      const md = (editor.storage as any).markdown?.getMarkdown?.() ?? "";
      onChange(md);
    },
    editorProps: {
      attributes: {
        // The editor's inner contenteditable element. Tailwind classes here
        // shape the typography of the rendered content.
        class:
          "tiptap-editor outline-none text-sm leading-relaxed text-ink-soft",
      },
      handleDOMEvents: {
        contextmenu: (_view, event) => {
          if (onContextMenu && editorRef.current) {
            onContextMenu(event as unknown as React.MouseEvent, editorRef.current);
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
    },
  });

  const editorRef = useRef<Editor | null>(null);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      editor?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <EditorContent editor={editor} className={className} />;
}
